// LoyaltyPanel — shows loyalty status in checkout for a RETURNING (non-guest)
// customer, and lets them join or redeem points/rewards.
//
// Rendering rules (per requirements):
//   - Renders nothing unless the venue/group has an ACTIVE program.
//   - Renders nothing for guests (no real customerID).
//   - If active + NOT enrolled  -> "Join our rewards program?" prompt.
//   - If active + enrolled      -> points/tier, active subscription discount,
//                                  and a redeem control (points / rewards / surprises).
//
// The parent owns the discount. This component reports the *selected* redemption
// via onRedemptionChange({ discount, points, reward_id, surprise_id, label }) or
// null when cleared. It also reports the subscription discount via
// onSubscriptionChange({ discount_percent, plan_name }) once, so checkout can
// apply it automatically. Commit + earn happen in checkout after the order posts.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

export function LoyaltyPanel({
  venueid,
  customer,               // must have a real customerID (>0) to show
  subtotal,               // current cart subtotal (number)
  onRedemptionChange,     // (redemption|null) => void
  onSubscriptionChange    // ({discount_percent, plan_name}|null) => void
}) {
  const customerID = customer && !customer.isGuest ? (customer.customerID || customer.customerId) : null;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');

  // Redemption UI state
  const [mode, setMode] = useState('none');      // none | points | reward | surprise
  const [pointsInput, setPointsInput] = useState('');
  const [selection, setSelection] = useState(null); // current applied redemption
  const [previewMsg, setPreviewMsg] = useState('');

  const load = useCallback(async () => {
    if (!venueid || !customerID) { setStatus(null); return; }
    setLoading(true); setError('');
    try {
      const res = await api.getLoyaltyStatus(venueid, customerID);
      setStatus(res && res.result === 1 ? res : null);
      if (res && res.subscription && res.subscription.discount_percent > 0) {
        onSubscriptionChange && onSubscriptionChange({
          discount_percent: res.subscription.discount_percent,
          plan_name: res.subscription.plan_name
        });
      } else {
        onSubscriptionChange && onSubscriptionChange(null);
      }
    } catch (e) {
      setError('Could not load rewards');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [venueid, customerID, onSubscriptionChange]);

  useEffect(() => { load(); }, [load]);

  // Clear any applied redemption if the cart changes materially.
  useEffect(() => {
    if (selection) { clearRedemption(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const clearRedemption = () => {
    setSelection(null);
    setMode('none');
    setPointsInput('');
    setPreviewMsg('');
    onRedemptionChange && onRedemptionChange(null);
  };

  const applyPreview = async (payload, label) => {
    setPreviewMsg('');
    try {
      const res = await api.loyaltyRedeemPreview({ venueid, customerID, subtotal, ...payload });
      if (!res || res.ok !== true) {
        setPreviewMsg((res && res.message) || 'Could not apply');
        return;
      }
      const redemption = {
        discount: Number(res.discount) || 0,
        points: payload.points || res.points_used || 0,
        reward_id: payload.reward_id || null,
        surprise_id: payload.surprise_id || null,
        label: label
      };
      setSelection(redemption);
      onRedemptionChange && onRedemptionChange(redemption);
    } catch (e) {
      setPreviewMsg('Could not apply redemption');
    }
  };

  const joinNow = async () => {
    setJoining(true); setError('');
    try {
      const res = await api.joinLoyalty(venueid, customerID);
      if (res && res.result === 1) { await load(); }
      else setError((res && res.message) || 'Could not join');
    } catch (e) { setError('Could not join'); }
    finally { setJoining(false); }
  };

  // ---- render gates ----
  if (!venueid || !customerID) return null;   // guests / no customer
  if (loading && !status) {
    return <div className="loyalty-panel loyalty-panel--loading" style={S.wrap}>Loading rewards…</div>;
  }
  if (!status || !status.active) return null;  // no active program

  const program = status.program || {};

  // Not enrolled -> join prompt
  if (!status.enrolled) {
    return (
      <div className="loyalty-panel" style={S.wrap}>
        <div style={S.headerRow}>
          <span style={S.title}>⭐ {program.program_name || 'Rewards'}</span>
        </div>
        <p style={S.muted}>
          Join {program.program_name || 'our rewards program'} to earn points on every order
          {program.signup_bonus > 0 ? ` — and get ${program.signup_bonus} bonus points now.` : '.'}
        </p>
        <button type="button" style={S.primaryBtn} onClick={joinNow} disabled={joining}>
          {joining ? 'Joining…' : 'Join & earn points'}
        </button>
        {error && <p style={S.err}>{error}</p>}
      </div>
    );
  }

  // Enrolled
  const rewards = (status.rewards || []).filter(r => r.affordable && (r.reward_type || '').indexOf('discount') === 0 || true);
  const surprises = status.surprises || [];
  const sub = status.subscription;

  return (
    <div className="loyalty-panel" style={S.wrap}>
      <div style={S.headerRow}>
        <span style={S.title}>⭐ {program.program_name || 'Rewards'}</span>
        <span style={S.points}>{status.points} pts</span>
      </div>
      {status.tier && status.tier.tier_name && (
        <div style={{ ...S.pill, background: status.tier.color || 'var(--primary,#b91c1c)' }}>
          {status.tier.tier_icon ? status.tier.tier_icon + ' ' : ''}{status.tier.tier_name}
        </div>
      )}

      {sub && sub.discount_percent > 0 && (
        <p style={S.subLine}>
          ✓ {sub.plan_name || 'Member'} — {sub.discount_percent}% off applied automatically
        </p>
      )}

      {/* Applied redemption summary */}
      {selection ? (
        <div style={S.appliedRow}>
          <span>{selection.label} · −${selection.discount.toFixed(2)}</span>
          <button type="button" style={S.linkBtn} onClick={clearRedemption}>Remove</button>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          {/* Points redemption */}
          {status.points >= (program.min_redemption_points || 0) && program.min_redemption_points >= 0 && (
            <div style={S.redeemBlock}>
              <label style={S.smallLabel}>Redeem points (min {program.min_redemption_points})</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number" min={program.min_redemption_points || 0} max={status.points} step="1"
                  value={pointsInput} onChange={e => setPointsInput(e.target.value)}
                  placeholder={String(program.min_redemption_points || 0)} style={S.input}
                />
                <button
                  type="button" style={S.secondaryBtn}
                  onClick={() => applyPreview({ points: parseInt(pointsInput, 10) || 0 },
                    `${parseInt(pointsInput, 10) || 0} pts redeemed`)}
                  disabled={!pointsInput}
                >Apply</button>
              </div>
              <span style={S.hint}>
                {(program.redemption_rate ? `Worth about $${((parseInt(pointsInput, 10) || 0) * program.redemption_rate).toFixed(2)}` : '')}
              </span>
            </div>
          )}

          {/* Reward catalog (affordable) */}
          {(status.rewards || []).some(r => r.affordable) && (
            <div style={S.redeemBlock}>
              <label style={S.smallLabel}>Use a reward</label>
              {(status.rewards || []).filter(r => r.affordable).map(r => (
                <button key={r.reward_id} type="button" style={S.rewardBtn}
                  onClick={() => applyPreview({ reward_id: r.reward_id }, r.reward_name)}>
                  <span>{r.reward_name}</span>
                  <span style={S.rewardCost}>{r.points_cost} pts</span>
                </button>
              ))}
            </div>
          )}

          {/* Surprise & delight */}
          {surprises.length > 0 && (
            <div style={S.redeemBlock}>
              <label style={S.smallLabel}>🎁 Surprise rewards</label>
              {surprises.map(s => (
                <button key={s.surprise_id} type="button" style={S.rewardBtn}
                  onClick={() => applyPreview({ surprise_id: s.surprise_id }, s.description || 'Surprise reward')}>
                  <span>{s.description || 'Surprise reward'}</span>
                  <span style={S.rewardCost}>Free</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {previewMsg && <p style={S.err}>{previewMsg}</p>}
      {error && <p style={S.err}>{error}</p>}
    </div>
  );
}

const S = {
  wrap: { border: '1px solid var(--border,#e5e7eb)', borderRadius: 12, padding: 16, marginBottom: 16, background: 'var(--bg,#fff)' },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontWeight: 700, fontSize: '1rem' },
  points: { fontWeight: 700, color: 'var(--primary,#b91c1c)' },
  pill: { display: 'inline-block', color: '#fff', fontSize: '0.75rem', fontWeight: 600, padding: '2px 10px', borderRadius: 999, marginTop: 8 },
  subLine: { margin: '8px 0 0', color: '#15803d', fontWeight: 600, fontSize: '0.9rem' },
  muted: { margin: '8px 0 12px', color: 'var(--text,#374151)', fontSize: '0.9rem', lineHeight: 1.4 },
  primaryBtn: { padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'var(--primary,#b91c1c)', color: '#fff', fontWeight: 600 },
  secondaryBtn: { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--primary,#b91c1c)', cursor: 'pointer', background: 'transparent', color: 'var(--primary,#b91c1c)', fontWeight: 600 },
  rewardBtn: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 10px', marginTop: 6, borderRadius: 8, border: '1px solid var(--border,#e5e7eb)', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' },
  rewardCost: { color: 'var(--primary,#b91c1c)', fontWeight: 600, whiteSpace: 'nowrap' },
  redeemBlock: { marginTop: 10 },
  smallLabel: { display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 },
  input: { flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border,#e5e7eb)' },
  hint: { display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: 4 },
  appliedRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(21,128,61,0.08)', color: '#15803d', fontWeight: 600, fontSize: '0.9rem' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--primary,#b91c1c)', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 },
  err: { color: '#b91c1c', fontSize: '0.85rem', marginTop: 8 }
};

export default LoyaltyPanel;